"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Check, Languages, Loader2 } from "lucide-react";
import { UI_LOCALES, UI_LOCALE_META, type UiLocale } from "@/lib/ui-i18n/config";
import { setUiLocaleAction } from "@/lib/ui-i18n/actions";
import { useT, useUiLocale } from "./provider";

function useSwitch() {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const change = (next: UiLocale | "auto", after?: () => void) =>
    start(async () => {
      setError(null);
      const r = await setUiLocaleAction(next);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      after?.();
      router.refresh();
    });
  return { pending, error, change };
}

/** Compact <select> (auth pages, footers). Each language is shown in its own name. */
export function LanguageSelect({ className = "", id = "ui-language" }: { className?: string; id?: string }) {
  const t = useT("common");
  const locale = useUiLocale();
  const { pending, error, change } = useSwitch();
  return (
    <div className={`inline-flex items-center gap-2 text-sm text-ink-600 ${className}`}>
      {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Languages className="h-4 w-4" aria-hidden />}
      <label htmlFor={id} className="sr-only">{t("language.label")}</label>
      <select id={id} className="input w-auto py-1 pe-8 text-sm" value={locale} disabled={pending} onChange={(e) => change(e.target.value as UiLocale)} data-testid="ui-language-select">
        {UI_LOCALES.map((l) => (
          <option key={l} value={l} lang={l}>{UI_LOCALE_META[l].native}</option>
        ))}
      </select>
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
    </div>
  );
}

/** Radio-style items for menus (dashboard user menu). */
export function LanguageMenuItems({ onDone }: { onDone?: () => void }) {
  const t = useT("common");
  const locale = useUiLocale();
  const { pending, error, change } = useSwitch();
  return (
    <div role="group" aria-label={t("language.label")} className="px-1.5 py-1">
      <div className="flex items-center gap-2 px-1.5 pb-1 text-[11px] font-semibold uppercase text-ink-400">
        <Languages className="h-3.5 w-3.5" aria-hidden /> {t("language.label")}
        {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
      </div>
      {UI_LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          role="menuitemradio"
          aria-checked={l === locale}
          lang={l}
          disabled={pending}
          onClick={() => (l === locale ? onDone?.() : change(l, onDone))}
          className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-start text-sm text-ink-700 hover:bg-ink-50 disabled:opacity-60"
          data-testid={`ui-language-${l}`}
        >
          <span>{UI_LOCALE_META[l].native}</span>
          {l === locale && <Check className="h-4 w-4 text-brand-600" aria-hidden />}
        </button>
      ))}
      {error && <p role="alert" className="px-3 text-xs text-red-700">{error}</p>}
    </div>
  );
}
